import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AppImage from '../../components/AppImage';
import { regionAssociationService } from '../../services/regionAssociationService';
import { useAuth } from '../../contexts/AuthContext';
import { TANZANIA_REGION_OPTIONS, isRegionAdmin } from '../../utils/tanzaniaRegions';

const REGION_OPTIONS = TANZANIA_REGION_OPTIONS;

const INITIAL_FORM = {
  associationName: '',
  region: '',
  postalAddress: '',
  physicalAddress: '',
  email: '',
  mobile: '',
  registeredDate: '',
  nextElectionDate: '',
  registrationNumber: '',
  constitutionFile: null,
  registrationCertificateFile: null,
  logoFile: null,
  chairpersonName: '',
  chairpersonEmail: '',
  chairpersonMobile: '',
  chairpersonPhotoFile: null,
  secretaryName: '',
  secretaryEmail: '',
  secretaryMobile: '',
  secretaryPhotoFile: null,
};

const getRegionLabel = (value) =>
  REGION_OPTIONS.find((r) => r.value === value)?.label || value || '—';

const statusClass = (status) => {
  const value = (status || 'pending').toLowerCase();
  if (value === 'active' || value === 'approved') return 'bg-success/10 text-success';
  if (value === 'rejected') return 'bg-destructive/10 text-destructive';
  return 'bg-muted text-muted-foreground';
};

const FileField = ({ label, accept, fileName, onChange, inputRef, error }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        iconName="Upload"
        iconPosition="left"
        onClick={() => inputRef.current?.click()}
      >
        Choose File
      </Button>
      <span className="text-sm text-muted-foreground truncate">
        {fileName || 'No file chosen'}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onChange}
      />
    </div>
    {error && <p className="text-sm text-destructive">{error}</p>}
  </div>
);

const RegionAssociationRegistration = () => {
  const { userProfile } = useAuth();
  const regionAdmin = isRegionAdmin(userProfile?.role);
  const assignedRegion = userProfile?.region || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingId, setEditingId] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [search, setSearch] = useState('');

  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM,
    region: '',
  }));
  const [previews, setPreviews] = useState({
    logo: null,
    chairpersonPhoto: null,
    secretaryPhoto: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const constitutionRef = useRef(null);
  const certificateRef = useRef(null);
  const logoRef = useRef(null);
  const chairpersonPhotoRef = useRef(null);
  const secretaryPhotoRef = useRef(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Region Association Registration' },
  ];

  const availableRegionOptions = useMemo(() => {
    if (regionAdmin && assignedRegion) {
      return REGION_OPTIONS.filter((option) => option.value === assignedRegion);
    }
    const taken = new Set(
      associations
        .filter((item) => !editingId || item.id !== editingId)
        .map((item) => item.region)
    );
    return REGION_OPTIONS.filter((option) => !taken.has(option.value));
  }, [regionAdmin, assignedRegion, associations, editingId]);

  const loadAssociations = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);
    try {
      const data = await regionAssociationService.getAll();
      const list = data || [];
      setAssociations(
        regionAdmin && assignedRegion
          ? list.filter((item) => item.region === assignedRegion)
          : list
      );
    } catch (err) {
      setListError(err.message || 'Failed to load registered regions');
    } finally {
      setIsLoadingList(false);
    }
  }, [regionAdmin, assignedRegion]);

  useEffect(() => {
    loadAssociations();
  }, [loadAssociations]);

  const filteredAssociations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return associations;
    return associations.filter((item) =>
      [
        item?.associationName,
        item?.region,
        getRegionLabel(item?.region),
        item?.email,
        item?.chairpersonName,
        item?.registrationStatus,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [associations, search]);

  const regionLabel = useMemo(
    () => getRegionLabel(formData.region),
    [formData.region]
  );

  const clearPreviews = () => {
    Object.values(previews).forEach((url) => {
      if (url?.startsWith?.('blob:')) URL.revokeObjectURL(url);
    });
    setPreviews({ logo: null, chairpersonPhoto: null, secretaryPhoto: null });
  };

  const resetForm = () => {
    clearPreviews();
    setFormData({
      ...INITIAL_FORM,
      region: regionAdmin ? assignedRegion : '',
    });
    setErrors({});
    setEditingId(null);
    setError(null);
  };

  const openAddForm = () => {
    resetForm();
    setSuccess(null);
    setView('form');
  };

  const openEditForm = (association) => {
    clearPreviews();
    setEditingId(association.id);
    setFormData({
      ...INITIAL_FORM,
      associationName: association.associationName || '',
      region: association.region || '',
      postalAddress: association.postalAddress || '',
      physicalAddress: association.physicalAddress || '',
      email: association.email || '',
      mobile: association.mobile || '',
      registeredDate: association.registeredDate || '',
      nextElectionDate: association.nextElectionDate || '',
      registrationNumber: association.registrationNumber || '',
      chairpersonName: association.chairpersonName || '',
      chairpersonEmail: association.chairpersonEmail || '',
      chairpersonMobile: association.chairpersonMobile || '',
      secretaryName: association.secretaryName || '',
      secretaryEmail: association.secretaryEmail || '',
      secretaryMobile: association.secretaryMobile || '',
    });
    setPreviews({
      logo: association.logoUrl || null,
      chairpersonPhoto: association.chairpersonPhotoUrl || null,
      secretaryPhoto: association.secretaryPhotoUrl || null,
    });
    setErrors({});
    setError(null);
    setSuccess(null);
    setView('form');
  };

  const backToList = () => {
    resetForm();
    setView('list');
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (field, previewKey, event) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, [field]: file }));
    if (previewKey) {
      if (previews[previewKey]?.startsWith?.('blob:')) {
        URL.revokeObjectURL(previews[previewKey]);
      }
      setPreviews((prev) => ({
        ...prev,
        [previewKey]: file ? URL.createObjectURL(file) : prev[previewKey],
      }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.associationName?.trim()) next.associationName = 'Association name is required';
    if (!formData.region) next.region = 'Region is required';
    if (!formData.email?.trim()) next.email = 'Email is required';
    if (!formData.mobile?.trim()) next.mobile = 'Mobile is required';
    if (!formData.chairpersonName?.trim()) next.chairpersonName = 'Chairperson name is required';
    if (!formData.secretaryName?.trim()) next.secretaryName = 'Secretary name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = editingId
        ? await regionAssociationService.update(editingId, formData)
        : await regionAssociationService.create(formData);

      if (result?.uploadWarning) {
        setSuccess(
          `Region association saved, but some files failed to upload (${result.uploadWarning}).`
        );
      } else {
        setSuccess(
          editingId
            ? 'Region association updated successfully.'
            : 'Region association registered successfully.'
        );
      }

      await loadAssociations();
      resetForm();
      setView('list');
    } catch (err) {
      setError(err.message || 'Failed to save region association information.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPin" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Region Association Registration
                  </h1>
                  <p className="text-muted-foreground">
                    {view === 'list'
                      ? 'Manage registered regional basketball associations'
                      : editingId
                        ? 'Update regional basketball association information'
                        : 'Register a new regional basketball association'}
                  </p>
                </div>
              </div>

              {view === 'list' ? (
                <Button iconName="Plus" iconPosition="left" onClick={openAddForm}>
                  Add Region
                </Button>
              ) : (
                <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={backToList}>
                  Back to List
                </Button>
              )}
            </div>
          </div>

          {success && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={18} />
                <p className="font-medium">{success}</p>
              </div>
            </div>
          )}

          {(error || listError) && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} />
                <p className="font-medium">{error || listError}</p>
              </div>
            </div>
          )}

          {view === 'list' ? (
            <div className="space-y-4">
              <div className="bg-card rounded-lg border border-border card-shadow p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 max-w-md">
                    <Input
                      type="search"
                      placeholder="Search by name, region, email..."
                      iconName="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {filteredAssociations.length} of {associations.length} registered
                  </p>
                </div>
              </div>

              {isLoadingList ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
                  <p className="text-muted-foreground">Loading registered regions...</p>
                </div>
              ) : filteredAssociations.length === 0 ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="MapPin" size={40} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {associations.length === 0 ? 'No Regions Registered' : 'No Matching Regions'}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {associations.length === 0
                      ? 'Get started by registering the first regional association.'
                      : 'Try adjusting your search.'}
                  </p>
                  {associations.length === 0 && (
                    <Button iconName="Plus" iconPosition="left" onClick={openAddForm}>
                      Add Region
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr className="text-left">
                          <th className="px-4 py-3 font-semibold text-foreground">Association</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Region</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Contact</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Leadership</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Status</th>
                          <th className="px-4 py-3 font-semibold text-foreground text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssociations.map((item) => (
                          <tr key={item.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-[200px]">
                                <div className="w-11 h-11 rounded-lg bg-white border border-border overflow-hidden flex items-center justify-center shrink-0">
                                  {item.logoUrl ? (
                                    <AppImage
                                      src={item.logoUrl}
                                      alt={item.associationName}
                                      className="w-full h-full object-contain p-0.5"
                                    />
                                  ) : (
                                    <Icon name="MapPin" size={18} className="text-primary" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <Link
                                    to={`/regions/${item.id}`}
                                    className="font-medium text-primary hover:underline truncate block"
                                    title="View regional profile"
                                  >
                                    {item.associationName || 'Unnamed'}
                                  </Link>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {item.registrationNumber
                                      ? `Reg. ${item.registrationNumber}`
                                      : 'No registration number'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-foreground capitalize whitespace-nowrap">
                              {getRegionLabel(item.region)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="min-w-[140px]">
                                <p className="text-foreground truncate">{item.email || '—'}</p>
                                <p className="text-xs text-muted-foreground">{item.mobile || '—'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="min-w-[140px]">
                                <p className="text-foreground truncate">
                                  {item.chairpersonName || '—'}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  Sec: {item.secretaryName || '—'}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex text-xs px-2.5 py-1 rounded-full capitalize ${statusClass(
                                  item.registrationStatus
                                )}`}
                              >
                                {item.registrationStatus || 'pending'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Pencil"
                                iconPosition="left"
                                onClick={() => openEditForm(item)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                <div className="border-b border-border px-6 py-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    {editingId ? 'Update Regional Information' : 'Register New Region'}
                  </h2>
                </div>

                <div className="p-6 border-b border-border">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                      <div className="rounded-lg border border-border p-4 h-full">
                        <p className="text-sm font-medium text-muted-foreground mb-3 text-center">
                          Association Logo
                        </p>
                        <div className="aspect-square rounded-lg border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                          {previews.logo ? (
                            <AppImage
                              src={previews.logo}
                              alt="Association logo preview"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="text-center p-4">
                              <Icon name="Image" size={40} className="text-muted-foreground mx-auto mb-2" />
                              <p className="text-xs text-muted-foreground">Logo preview</p>
                            </div>
                          )}
                        </div>
                        {formData.associationName && (
                          <p className="mt-3 text-sm font-medium text-foreground text-center">
                            {formData.associationName}
                          </p>
                        )}
                        {formData.region && (
                          <p className="text-xs text-muted-foreground text-center">{regionLabel}</p>
                        )}
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <fieldset className="rounded-lg border border-border p-4 md:p-6">
                        <legend className="px-2 text-sm font-semibold text-foreground">
                          Association Information
                        </legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Association Name"
                            required
                            value={formData.associationName}
                            onChange={(e) => handleChange('associationName', e.target.value)}
                            error={errors.associationName}
                            placeholder="e.g. Dar es Salaam Basketball"
                          />
                          <Select
                            label="Region"
                            required
                            options={availableRegionOptions}
                            value={formData.region}
                            onChange={(value) => handleChange('region', value)}
                            error={errors.region}
                            placeholder="Select region"
                            searchable
                            disabled={regionAdmin}
                            description={
                              regionAdmin
                                ? 'Locked to your assigned region'
                                : undefined
                            }
                          />
                          <Input
                            label="Postal Address"
                            value={formData.postalAddress}
                            onChange={(e) => handleChange('postalAddress', e.target.value)}
                            placeholder="e.g. Box 6244 DSM"
                          />
                          <Input
                            label="Physical Address"
                            value={formData.physicalAddress}
                            onChange={(e) => handleChange('physicalAddress', e.target.value)}
                            placeholder="e.g. National Indoor Stadium"
                          />
                          <Input
                            label="Email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            error={errors.email}
                            placeholder="association@example.com"
                          />
                          <Input
                            label="Mobile"
                            required
                            value={formData.mobile}
                            onChange={(e) => handleChange('mobile', e.target.value)}
                            error={errors.mobile}
                            placeholder="07XXXXXXXX"
                          />
                          <Input
                            label="Registered Date"
                            type="date"
                            value={formData.registeredDate}
                            onChange={(e) => handleChange('registeredDate', e.target.value)}
                          />
                          <Input
                            label="Date Next Election"
                            type="date"
                            value={formData.nextElectionDate}
                            onChange={(e) => handleChange('nextElectionDate', e.target.value)}
                          />
                          <FileField
                            label="Constitution"
                            accept=".pdf,.doc,.docx,image/*"
                            fileName={formData.constitutionFile?.name}
                            inputRef={constitutionRef}
                            onChange={(e) => handleFileChange('constitutionFile', null, e)}
                          />
                          <FileField
                            label="Registration Certificate"
                            accept=".pdf,.doc,.docx,image/*"
                            fileName={formData.registrationCertificateFile?.name}
                            inputRef={certificateRef}
                            onChange={(e) => handleFileChange('registrationCertificateFile', null, e)}
                          />
                          <FileField
                            label="Association Logo"
                            accept="image/*"
                            fileName={formData.logoFile?.name}
                            inputRef={logoRef}
                            onChange={(e) => handleFileChange('logoFile', 'logo', e)}
                          />
                          <Input
                            label="Registration Number"
                            value={formData.registrationNumber}
                            onChange={(e) => handleChange('registrationNumber', e.target.value)}
                            placeholder="e.g. 1234"
                          />
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                      <div className="grid grid-cols-2 gap-4 h-full">
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2 text-center">
                            Chairperson Photo
                          </p>
                          <div className="aspect-[3/4] rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                            {previews.chairpersonPhoto ? (
                              <AppImage
                                src={previews.chairpersonPhoto}
                                alt="Chairperson preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Icon name="User" size={28} className="text-muted-foreground" />
                            )}
                          </div>
                          <p className="mt-2 text-xs text-foreground text-center font-medium truncate">
                            {formData.chairpersonName || 'Chairperson'}
                          </p>
                          <p className="text-xs text-muted-foreground text-center truncate">
                            {formData.chairpersonMobile || '—'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-border p-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2 text-center">
                            Secretary Photo
                          </p>
                          <div className="aspect-[3/4] rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                            {previews.secretaryPhoto ? (
                              <AppImage
                                src={previews.secretaryPhoto}
                                alt="Secretary preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Icon name="User" size={28} className="text-muted-foreground" />
                            )}
                          </div>
                          <p className="mt-2 text-xs text-foreground text-center font-medium truncate">
                            {formData.secretaryName || 'Secretary'}
                          </p>
                          <p className="text-xs text-muted-foreground text-center truncate">
                            {formData.secretaryMobile || '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <fieldset className="rounded-lg border border-border p-4 md:p-6">
                        <legend className="px-2 text-sm font-semibold text-foreground">
                          Leadership Information
                        </legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Chairperson"
                            required
                            value={formData.chairpersonName}
                            onChange={(e) => handleChange('chairpersonName', e.target.value)}
                            error={errors.chairpersonName}
                            placeholder="Full name"
                          />
                          <Input
                            label="Secretary"
                            required
                            value={formData.secretaryName}
                            onChange={(e) => handleChange('secretaryName', e.target.value)}
                            error={errors.secretaryName}
                            placeholder="Full name"
                          />
                          <Input
                            label="Chairperson Email"
                            type="email"
                            value={formData.chairpersonEmail}
                            onChange={(e) => handleChange('chairpersonEmail', e.target.value)}
                            placeholder="email@example.com"
                          />
                          <Input
                            label="Secretary Email"
                            type="email"
                            value={formData.secretaryEmail}
                            onChange={(e) => handleChange('secretaryEmail', e.target.value)}
                            placeholder="email@example.com"
                          />
                          <Input
                            label="Chairperson Mobile"
                            value={formData.chairpersonMobile}
                            onChange={(e) => handleChange('chairpersonMobile', e.target.value)}
                            placeholder="07XXXXXXXX"
                          />
                          <Input
                            label="Secretary Mobile"
                            value={formData.secretaryMobile}
                            onChange={(e) => handleChange('secretaryMobile', e.target.value)}
                            placeholder="07XXXXXXXX"
                          />
                          <FileField
                            label="Chairperson Photo"
                            accept="image/*"
                            fileName={formData.chairpersonPhotoFile?.name}
                            inputRef={chairpersonPhotoRef}
                            onChange={(e) =>
                              handleFileChange('chairpersonPhotoFile', 'chairpersonPhoto', e)
                            }
                          />
                          <FileField
                            label="Secretary Photo"
                            accept="image/*"
                            fileName={formData.secretaryPhotoFile?.name}
                            inputRef={secretaryPhotoRef}
                            onChange={(e) =>
                              handleFileChange('secretaryPhotoFile', 'secretaryPhoto', e)
                            }
                          />
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={backToList}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  iconName="Save"
                  iconPosition="left"
                >
                  {editingId ? 'Save Changes' : 'Save Regional Information'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegionAssociationRegistration;
